import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Lightbulb, 
  Calendar,
  ChevronRight,
  Sparkles,
  TrendingUp,
  Filter
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';
import { PageHeader, TabGroup, HTCard, HTEmptyState, StatusBadge } from '../../../components/HumaTwinUI';

export default function DailyReflections() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('recent');
  const [searchQuery, setSearchQuery] = useState('');

  const tabs = [
    { id: 'recent', label: t('reflections.tabs.recent') || 'Recent' },
    { id: 'themes', label: t('reflections.tabs.themes') || 'Themes' },
    { id: 'insights', label: t('reflections.tabs.insights') || 'Insights' },
    { id: 'growth', label: t('reflections.tabs.growth') || 'Growth' },
  ];

  useEffect(() => {
    fetchReflections();
  }, [activeTab]);

  const fetchReflections = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/daily-reflection/list');
      setReflections(response.data || []);
    } catch (error) {
      console.error('Failed to fetch reflections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewReflection = () => {
    navigate('/personal/assistant?prompt=daily-reflection');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredReflections = reflections.filter(r => 
    !searchQuery || r.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.content?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="reflections-page">
        <Skeleton className="h-10 w-48 mb-2" />
        <Skeleton className="h-5 w-72 mb-8" />
        <Skeleton className="h-12 w-full mb-6" />
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="reflections-page">
      {/* Header */}
      <PageHeader
        title={t('reflections.title') || 'Reflections'}
        subtitle={t('reflections.subtitle') || 'A space for deeper thinking'}
        actionLabel={t('reflections.newReflection') || '+ New Reflection'}
        actionIcon={Plus}
        onAction={handleNewReflection}
      />

      {/* Tabs */}
      <div className="mb-6">
        <TabGroup 
          tabs={tabs} 
          activeTab={activeTab} 
          onTabChange={setActiveTab} 
        />
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          type="text"
          placeholder={t('common.search') || 'Search...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 rounded-xl"
          data-testid="reflections-search"
        />
      </div>

      {/* Reflections List */}
      {filteredReflections.length === 0 ? (
        <HTEmptyState
          icon={Lightbulb}
          title={t('reflections.empty.title') || 'No reflections yet'}
          description={t('reflections.empty.description') || 'Start your first reflection to capture your thoughts and insights.'}
          actionLabel={t('reflections.empty.action') || 'Start Reflecting'}
          onAction={handleNewReflection}
        />
      ) : (
        <div className="space-y-3">
          {filteredReflections.map((reflection, index) => (
            <HTCard
              key={reflection.id || index}
              className="p-4 cursor-pointer hover:shadow-card-hover transition-all group"
              onClick={() => navigate(`/personal/assistant/threads/${reflection.conversationId}`)}
              data-testid={`reflection-${index}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-100 transition-colors">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <h3 className="font-medium text-gray-900 truncate">
                      {reflection.title || t('reflections.untitled') || 'Untitled Reflection'}
                    </h3>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {formatDate(reflection.date || reflection.createdAt)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 line-clamp-2">
                    {reflection.summary || reflection.content?.substring(0, 150) || 
                     t('reflections.noSummary') || 'No summary available'}
                  </p>
                  {reflection.themes && reflection.themes.length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {reflection.themes.slice(0, 3).map((theme, i) => (
                        <StatusBadge key={i} variant="purple">
                          {theme}
                        </StatusBadge>
                      ))}
                    </div>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
              </div>
            </HTCard>
          ))}
        </div>
      )}
    </div>
  );
}
